import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges
} from '@angular/core'
import { CondOperator, RequestQueryBuilder } from '@nestjsx/crud-request'
import { UntilDestroy } from '@ngneat/until-destroy'
import { Dictionary } from '@ngrx/entity'

import { Store } from '@ngrx/store'
import moment from 'moment-mini'
import { combineLatest, Observable } from 'rxjs'
import {
  filter,
  first,
  map,
  switchMap,
  take,
  tap,
  withLatestFrom
} from 'rxjs/operators'
import { AppState } from 'src/app/core/core.state'
import { settingSelectors } from 'src/app/core/settings/settings.selectors'
import { Booking } from 'src/app/features/booking/booking'
import { bookingSelectors } from 'src/app/features/booking/booking.selectors'
import { Product } from 'src/app/features/product/product'
import { productSelectors } from 'src/app/features/product/product.selectors'
import { CompetitionMatch } from '../../competition-match/competition-match'
import { competitionMatchActions } from '../../competition-match/competition-match.actions'
import { competitionMatchSelectors } from '../../competition-match/competition-match.selectors'
import { CompetitionPool } from '../../competition-pool/competition-pool'
import { competitionPoolActions } from '../../competition-pool/competition-pool.actions'
import { competitionPoolSelectors } from '../../competition-pool/competition-pool.selectors'
import { CompetitionTeam } from '../competition-team'
import { competitionTeamSelectors } from '../competition-team.selectors'
import { ActivatedRoute } from '@angular/router'
@UntilDestroy()
@Component({
  selector: 'results-tab',
  templateUrl: './show.component.html',
  styleUrls: ['./show.component.scss']
})
export class CompetitionTeamResultsTabComponent implements OnInit, OnChanges {
  @Input() competitionId: number
  @Input() public filteredTeams: number[]
  public isLoading$: Observable<boolean>
  public matches$: Observable<CompetitionMatch[]>
  public selectedLanguage$: Observable<string>
  public competitionTeamEntities$: Observable<Dictionary<CompetitionTeam>>
  public bookingEntities$: Observable<Dictionary<Booking>>
  public teamId$: Observable<number>
  public team$: Observable<CompetitionTeam>
  public productEntities$: Observable<Dictionary<Product>>
  public competitionPool$: Observable<CompetitionPool>
  public teamIdsOfTheCompetitionPool: number[] = []
  private isCompetitionPoolFetched: boolean = false

  constructor(private store: Store<AppState>, private route: ActivatedRoute) {}

  ngOnInit() {
    this.selectedLanguage$ = this.store.select(
      settingSelectors.selectById,
      'language'
    )

    if (!this.competitionId) {
      this.initForTeam()
    } else {
      this.initForCompetition()
    }
  }
  ngOnChanges(changes: SimpleChanges) {
    if (changes['filteredTeams'] && !changes['filteredTeams'].firstChange) {
      this.refreshMatches()
    }
  }

  private initForCompetition() {
    this.competitionTeamEntities$ = this.store.select(
      competitionTeamSelectors.selectEntities
    )

    this.bookingEntities$ = this.store.select(bookingSelectors.selectEntities)

    this.productEntities$ = this.store.select(productSelectors.selectEntities)

    this.store.dispatch(
      competitionMatchActions.fetchMany({
        params: RequestQueryBuilder.create({
          search: {
            competitionId: this.competitionId,
            'booking.startDate': {
              [CondOperator.LOWER_THAN]: moment().toISOString()
            }
          },
          join: [
            { field: 'booking' },
            { field: 'booking.product' },
            { field: 'team1' },
            { field: 'team2' }
          ]
        })
      })
    )
    if (!this.filteredTeams?.length) {
      this.refreshMatches()
    }
  }

  private initForTeam() {
    this.teamId$ = this.route.paramMap.pipe(
      map((params) => params.get('teamId')),
      map((id) => Number(id)),
      filter((id): id is number => !isNaN(id))
    )

    this.team$ = this.teamId$.pipe(
      switchMap((teamId) =>
        this.store.select(competitionTeamSelectors.selectById, teamId)
      ),
      tap((team) => {
        if (this.isCompetitionPoolFetched) {
          return
        }
        this.isCompetitionPoolFetched = true
        this.store.dispatch(
          competitionPoolActions.fetchMany({
            params: RequestQueryBuilder.create({
              search: {
                competitionId: team?.competitionId
              },
              join: [{ field: 'competitionTeam' }]
            })
          })
        )
      })
    )

    this.competitionTeamEntities$ = this.store.select(
      competitionTeamSelectors.selectEntities
    )

    this.bookingEntities$ = this.store.select(bookingSelectors.selectEntities)

    this.productEntities$ = this.store.select(productSelectors.selectEntities)

    this.competitionPool$ = this.teamId$.pipe(
      switchMap((teamId) =>
        this.store.select(competitionPoolSelectors.selectByFilter, {
          field: 'competitionTeamId',
          value: teamId
        })
      ),
      withLatestFrom(this.team$),
      map(([competitionPools, team]) => {
        return competitionPools.filter(
          (competitionPool) =>
            competitionPool?.competitionId == team.competitionId
        )[0]
      })
    )

    this.competitionPool$
      .pipe(first((competitionPool) => Boolean(competitionPool)))
      .subscribe((competitionPool) => {
        this.store
          .select(competitionPoolSelectors.selectByFilter, {
            field: 'name',
            value: competitionPool?.name
          })
          .pipe(first((competitionPools) => Boolean(competitionPools)))
          .subscribe((competitionPools) => {
            competitionPools.forEach((competitionPool) => {
              this.teamIdsOfTheCompetitionPool.push(
                competitionPool.competitionTeamId
              )
            })

            this.store.dispatch(
              competitionMatchActions.fetchMany({
                params: RequestQueryBuilder.create({
                  search: {
                    $or: [
                      {
                        team1Id: {
                          [CondOperator.IN]: this.teamIdsOfTheCompetitionPool
                        }
                      },
                      {
                        team2Id: {
                          [CondOperator.IN]: this.teamIdsOfTheCompetitionPool
                        }
                      }
                    ]
                  },
                  join: [
                    { field: 'booking' },
                    { field: 'booking.product' },
                    { field: 'team1' },
                    { field: 'team2' }
                  ]
                })
              })
            )
          })
      })

    this.matches$ = combineLatest([
      this.store.select(competitionMatchSelectors.selectAll),
      this.bookingEntities$
    ]).pipe(
      map(([matches, bookingEntities]) =>
        matches?.filter(
          (match) =>
            moment(bookingEntities[match?.bookingId]?.startDate).isBefore(
              moment()
            ) &&
            (this.teamIdsOfTheCompetitionPool.includes(match.team1Id) ||
              this.teamIdsOfTheCompetitionPool.includes(match.team2Id))
        )
      )
    )
  }

  refreshMatches() {
    this.matches$ = combineLatest([
      this.store.select(competitionMatchSelectors.selectAll),
      this.bookingEntities$
    ]).pipe(
      map(([matches, bookingEntities]) => {
        if (this.filteredTeams?.length) {
          matches = matches.filter(
            (match) =>
              this.filteredTeams.includes(match.team1Id) ||
              this.filteredTeams.includes(match.team2Id)
          )
        }
        return matches
          ?.filter((match) => {
            if (match.competitionId != this.competitionId) {
              return false
            }

            return moment(
              bookingEntities[match?.bookingId]?.startDate
            ).isBefore(moment())
          })
          .sort(
            (a, b) =>
              moment(bookingEntities[b.bookingId]?.startDate).valueOf() -
              moment(bookingEntities[a.bookingId]?.startDate).valueOf()
          )
      })
    )
  }
}
